const OUTGOING_SENDERS = new Set(['bot', 'assistant', 'agent', 'system']);

export const getConversationMessageRole = (message = {}) => {
  const sender = String(message.sender || '').trim().toLowerCase();
  return OUTGOING_SENDERS.has(sender) ? 'bot' : 'user';
};

/** Pick the adjacent conversational exchange for the selected transcript message. */
export const getMessageExchange = (messages = [], selectedMessage = null) => {
  if (!selectedMessage) return { userMessage: null, botMessage: null };
  const selectedIndex = messages.findIndex((message) => message?._id === selectedMessage._id);
  const role = getConversationMessageRole(selectedMessage);

  if (role === 'user') {
    const followingMessages = selectedIndex >= 0 ? messages.slice(selectedIndex + 1) : [];
    const nextUserIndex = followingMessages.findIndex((message) => getConversationMessageRole(message) === 'user');
    const exchangeWindow = nextUserIndex < 0 ? followingMessages : followingMessages.slice(0, nextUserIndex);
    const botMessage = exchangeWindow.find((message) => getConversationMessageRole(message) === 'bot') || null;
    return { userMessage: selectedMessage, botMessage };
  }

  const previousMessages = selectedIndex > 0 ? messages.slice(0, selectedIndex).reverse() : [];
  const userMessage = previousMessages.find((message) => getConversationMessageRole(message) === 'user') || null;
  return { userMessage, botMessage: selectedMessage };
};

/** Return a human-readable selected choice only for choice-based flow nodes. */
export const getSelectedChoice = (steps = [], selectedMessage = null) => {
  const step = steps.find((item) => item?.relation === 'selected');
  const flowChoice = step && ['quick_reply', 'list_message'].includes(String(step.nodeType || '').toLowerCase())
    ? step.meta?.acceptedOption
    || step.output?.selectedItem?.title
    || step.output?.selectedItem?.label
    || step.output?.selectedItem?.value
    || step.output?.value
    : '';
  const storedChoice = selectedMessage?.metadata?.interactiveReply?.title
    || selectedMessage?.metadata?.whatsappInteractiveReply?.title;
  const choice = flowChoice || storedChoice;
  return typeof choice === 'string' || typeof choice === 'number' ? String(choice) : '';
};

/** Parse JSON previews written by the API interaction logger without leaking nested escaped JSON into the UI. */
const parsePreview = (value, depth = 0) => {
  if (depth > 3 || value === null || value === undefined) return value;
  if (typeof value === 'string') {
    try {
      return parsePreview(JSON.parse(value), depth + 1);
    } catch (_) {
      return value;
    }
  }
  if (Array.isArray(value)) return value.map((item) => parsePreview(item, depth + 1));
  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, parsePreview(item, depth + 1)]));
  }
  return value;
};

/** Convert provider request/response records into concise, human-readable conversation diagnostics. */
export const getInteractionPresentation = (log = {}) => {
  const request = parsePreview(log.request) || {};
  const response = parsePreview(log.response) || {};
  const event = String(log.event || '').toLowerCase();
  const direction = String(log.direction || '').toLowerCase();
  const failed = direction === 'error' || String(log.status || '').toLowerCase() === 'error';
  const messages = Array.isArray(response.messages) ? response.messages : [];
  const providerId = messages[0]?.id || response.messageId || '';

  if (failed) {
    return {
      title: 'Bot reply failed',
      detail: String(log.error || response.error?.message || response.message || 'The WhatsApp provider returned an error.'),
      outcome: 'Failed',
    };
  }
  if (event === 'whatsapp_incoming_message' || direction === 'inbound') {
    return {
      title: 'User message received',
      detail: String(request.metadata?.interactiveReply?.title || request.interactiveReply?.title || request.message || 'WhatsApp event received'),
      outcome: 'Received',
    };
  }
  if (direction === 'response') {
    return {
      title: 'WhatsApp provider response',
      detail: providerId ? `Provider accepted the message · ID …${String(providerId).slice(-10)}` : 'Provider response received; delivery receipt may follow.',
      outcome: 'Accepted',
    };
  }
  if (event.includes('send_template') || request.type === 'template') {
    return {
      title: 'Bot sent a template',
      detail: String(request.template?.name || 'WhatsApp template'),
      outcome: 'Sent',
    };
  }
  if (request.interactive) {
    const interactive = request.interactive;
    const options = (interactive.action?.sections || []).flatMap((section) => section.rows || [])
      .concat((interactive.action?.buttons || []).map((button) => button.reply || button));
    return {
      title: `Bot sent interactive ${interactive.type || 'message'}`,
      detail: [interactive.body?.text, options.map((option) => option.title).filter(Boolean).join(' · ')].filter(Boolean).join(' — ') || 'Interactive options sent',
      outcome: 'Sent',
    };
  }
  if (event.includes('send_message') || request.type === 'text') {
    return {
      title: 'Bot reply sent',
      detail: String(request.text?.body || request.text || 'WhatsApp message'),
      outcome: 'Sent',
    };
  }
  return { title: String(log.event || 'WhatsApp activity'), detail: 'Activity recorded', outcome: 'OK' };
};
